import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { COLORS } from 'theme'
import Button from 'components/Button'
import BackArrow from 'components/icons/BackArrow'
import CaretRight from 'components/icons/CaretRight'
import AddStudentIcon from 'components/icons/AddIcon'
import CollegeCard from 'components/CollegeCard'
import StudentsTable from '../StudentsTable'
import CallSchedulePanel from '../CallSchedule'
import EnhancedBulkUploadDrawer from '../CreateAssessment/Drawers/EnhancedBulkUploadDrawer'
import { message } from 'antd'
import axios from 'axios'
import bulkAddStudentsToAssessment from 'utils/bulkAddStudentsToAssessment'

import {
    SectionWrapper,
    TopHeader,
    TopSection,
    ButtonContainer,
    ContentSection,
    FormSection,
    SubHeading,
    FormRow,
    FormGroup,
    StyledLabel,
    Select,
    StatusContainer,
    StatusItem,
    HelperText,
    HeaderContainer,
    AssessmentInfoContainer,
    FilterSummaryBar,
    SummaryText,
    ClearFilterButton,
} from './styles'

/** Attempt buckets. Clicking one filters the student table below. */
const ATTEMPT_STAGES = [
    { key: 'sent', label: 'Assessment Sent' },
    { key: 'pending', label: 'Not started' },
    { key: 'inprogress', label: 'In progress' },
    { key: 'droppedoff', label: 'Dropped off' },
    { key: 'completed', label: 'Completed' },
]

const PART_TYPE_LABELS = {
    Aptitude: 'Aptitude',
    Communication: 'Communication',
    Hinglish: 'Communication',
    Role_Based: 'Role Based',
    AI_Interview: 'AI Interview',
    AI_Interview_v2: 'Complete AI Interview',
    Custom_Assessment: 'Custom',
    Behavior: 'Behaviour',
}
const partTypeLabel = type => PART_TYPE_LABELS[type] || String(type || '').replace(/_/g, ' ')

const AssessmentDetails = ({
    assessment,
    onClose,
    loading,
    assessmentFilters,
    instituteDomain,
    instituteDegree,
    instituteDepartment,
    instituteSpecialization,
    setAssessmentFilters,
    studentDataSent,
    studentDataPending,
    studentDataInProgress,
    studentDataDroppedOff,
    studentDataCompleted,
    studentDataLoading,
    fetchStudentsByStatus,
    fetchAllStudentStatusCounts,
    exportStudentData,
    sendRemindersToStudents,
    resendInvitesToStudents,
    assessmentInfo,
    entityType,
    specificAssessmentStudentList,
    fetchSpecificAssessmentStudentList,
    /** Opens one assessment of a Mix & Match float on its own screen. */
    onOpenPart,
}) => {
    // State variables for form fields - initialize from Redux store when available
    // //console.log("//console.log(assessment?.allowProctoring)", assessment?.allowProctoring)
    const formatCefrLevel = cefrLevel => {
        if (!cefrLevel) return 'Not Available'
        return cefrLevel.toUpperCase()
    }

    const cefrLevel = assessmentInfo?.cefrLevel
    // const cefrLevel = 'B1';
    const [searchQuery, setSearchQuery] = useState('')

    const [passingYear, setPassingYear] = useState(
        assessmentFilters?.passingYear || ''
    )
    const [filteredStudents, setFilteredStudents] = useState([])
    const [specialization, setSpecialization] = useState(
        assessmentFilters?.specialization || ''
    )
    const [allFiltersSelected, setAllFiltersSelected] = useState(false)
    const [activeStatusItem, setActiveStatusItem] = useState('sent')
    const [dataReady, setDataReady] = useState(false)

    const [addStudentDrawerOpen, setAddStudentDrawerOpen] = useState(false)

    const handleSaveStudents = async (studentsData) => {
        const progressKey = 'add-students-progress'
        try {
            const basePayload = {
                assessmentId: assessment.id,
                assessmentInstituteMapId: assessment.id,
                instituteCampusId: assessment?.instituteCampusId || assessment?.campusId,
                entityType: entityType,
            }

            const { addedCount, skippedCount, failedCount, chunkErrors } = await bulkAddStudentsToAssessment({
                axiosClient: axios,
                url: `${process.env.ADMIN_API_URL}assessment/addStudentsToAssessment`,
                basePayload,
                bulkUploadData: studentsData,
                chunkSize: 20,
                onProgress: ({ chunkIndex, totalChunks, addedSoFar, done }) => {
                    if (done) {
                        message.destroy(progressKey)
                        return
                    }
                    message.loading({
                        content: `Adding students… batch ${Math.min(chunkIndex + 1, totalChunks)} of ${totalChunks} (${addedSoFar} added)`,
                        key: progressKey,
                        duration: 0,
                    })
                },
            })

            if (failedCount === 0) {
                message.success(`Added ${addedCount} student(s)${skippedCount > 0 ? `, ${skippedCount} duplicate(s) skipped` : ''}`)
            } else {
                const firstErr = chunkErrors[0]?.message || 'unknown error'
                message.warning(`Added ${addedCount}, skipped ${skippedCount}, ${failedCount} failed (${firstErr}). Re-upload the failed rows to retry.`)
            }

            setAddStudentDrawerOpen(false)

            // Refresh data
            fetchAllStudentStatusCounts(assessment.id, entityType)
            fetchStudentsByStatus(assessment.id, activeStatusItem, entityType)

        } catch (error) {
            message.destroy(progressKey)
            console.error('Error adding students:', error)
            message.error(error.response?.data?.message || 'Failed to add students')
        }
    }

    // Local state for table pagination
    const [studentsPage, setStudentsPage] = useState(0)
    const [studentsLimit] = useState(10)

    // You can extend or modify these arrays to use data from Redux store
    const passingYears = ['2021', '2022', '2023', '2024']
    const specializations =
        instituteSpecialization && instituteSpecialization.length > 0
            ? instituteSpecialization
            : ['AI', 'Networking', 'Robotics']

    // Update both local state and Redux store
    const updateFilter = (field, value) => {
        setAssessmentFilters({
            ...assessmentFilters,
            [field]: value,
        })
    }
    const debounce = (fn, delay) => {
        let timer
        return function () {
            let context = this, args = arguments
            clearTimeout(timer)
            timer = setTimeout(() => { fn.apply(context, args) }, delay)
        }
    }
    const handleSearch = useCallback((query) => {
        setSearchQuery(query);
        setStudentsPage(0);
        if (assessment?.id) {
            fetchStudentsByStatus(assessment.id, activeStatusItem, entityType, query);
        }
    }, [assessment?.id, activeStatusItem, entityType, fetchStudentsByStatus]);
    const handleClearFilters = () => {
        setPassingYear('')
        setSpecialization('')
        setSearchQuery('')

        // Update Redux store too
        setAssessmentFilters({
            passingYear: '',
            specialization: '',
        })

        // Refresh data without search query
        fetchStudentsByStatus(assessment.id, activeStatusItem, entityType, '')
    }

    useEffect(() => {
        if (assessment?.id) {
            // Fetch data for all status types immediately
            fetchAllStudentStatusCounts(assessment.id, entityType)

            // Fetch data for the active status item (initially 'sent')
            fetchStudentsByStatus(assessment.id, activeStatusItem, entityType)
        }
    }, [assessment?.id, entityType])

    useEffect(() => {
        if (assessment?.id) {
            // fetchStudentsByStatus(assessment.id, activeStatusItem, entityType) // This usage relies on default search=''
        }
    }, [activeStatusItem, assessment?.id, entityType])

    const getFilterSummaryText = () => {
        return (
            <>
                Selected <strong>{passingYear}</strong> as passing year
            </>
        )
    }

    useEffect(() => {
        const currentData = getStudentDataForStatus()
        let filtered = Array.isArray(currentData) ? [...currentData] : []

        if (passingYear) {
            filtered = filtered.filter(
                student => student.passingYear === passingYear
            )
        }

        if (specialization) {
            filtered = filtered.filter(
                student => student.specialization === specialization
            )
        }

        setFilteredStudents(filtered)
    }, [
        passingYear,
        specialization,
        studentDataSent,
        studentDataPending,
        studentDataInProgress,
        studentDataDroppedOff,
        studentDataCompleted,
        activeStatusItem,
    ])

    // Reset form if assessment changes
    useEffect(() => {
        if (assessment) {
            // Reset both local state and Redux store
            setPassingYear('')
            setSpecialization('')

            // Reset filters in Redux
            setAssessmentFilters({
                passingYear: '',
                specialization: '',
            })
        }
    }, [assessment])

    useEffect(() => {
        if (passingYear) {
            setAllFiltersSelected(true)
        } else {
            setAllFiltersSelected(false)
        }
    }, [passingYear])

    const handleStatusItemClick = itemName => {
        setActiveStatusItem(itemName)

        // Only fetch data if all filters are set
        if (dataReady && assessment?.id) {
            fetchStudentsByStatus(assessment.id, itemName, entityType)
        }
    }

    // Keyed by ATTEMPT_STAGES.key so the strip can render from config rather
    // than five near-identical hand-written blocks.
    const attemptDataByStatus = useMemo(
        () => ({
            sent: studentDataSent || [],
            pending: studentDataPending || [],
            inprogress: studentDataInProgress || [],
            droppedoff: studentDataDroppedOff || [],
            completed: studentDataCompleted || [],
        }),
        [
            studentDataSent,
            studentDataPending,
            studentDataInProgress,
            studentDataDroppedOff,
            studentDataCompleted,
        ]
    )

    const getStudentDataForStatus = () => attemptDataByStatus[activeStatusItem] || []

    const attemptCounts = useMemo(
        () =>
            ATTEMPT_STAGES.reduce(
                (counts, stage) => ({
                    ...counts,
                    [stage.key]: attemptDataByStatus[stage.key].length,
                }),
                {}
            ),
        [attemptDataByStatus]
    )

    // Get assessment type display name
    const getAssessmentTypeDisplay = type => {
        const typeMap = {
            communication: 'Communication Assessment',
            behavior: 'Behavioral Assessment',
            cognitive: 'Cognitive Aptitude',
            tech_mcq: 'Technical Assessment - MCQ',
            tech_coding: 'Technical Assessment - Coding',
            Mix_Match: 'Mix & Match Assessment',
        }
        return typeMap[type] || type
    }

    /* The assessments a float is made of. They stay openable in their own
       right — the float collects them, it does not replace them, and a part's
       own screen carries what the float's summary cannot (its proctoring
       report, its invites, its export). */
    const mixMatchParts = assessmentInfo?.parts || []
    const isMixMatch = String(assessment?.assessmentType || '').toLowerCase() === 'mix_match'
    /* An on-call AI Interview's calls (planned times, outcomes, edits). The panel
       asks admin-node whether the map is on call and renders nothing otherwise. */
    const callScheduleMapId = assessment?.assessmentType === 'AI_Interview'
        ? assessment?.id
        : mixMatchParts.find(part => part.typeName === 'AI_Interview')?.mapId || null


    // Add debug log to check the assessment type
    useEffect(() => {
        if (assessment) {
            //console.log("Assessment type in AssessmentDetails:", assessment.assessmentType);
        }
    }, [assessment])

    if (!assessment) {
        return (
            <SectionWrapper column>
                <HeaderContainer>
                    <TopSection>
                        <TopHeader>Subscribed Institute</TopHeader>
                        <ButtonContainer>
                            <Button.Secondary text="Back" onClick={onClose} />
                        </ButtonContainer>
                    </TopSection>
                </HeaderContainer>
                <ContentSection>
                    <div>Loading assessment details...</div>
                </ContentSection>
            </SectionWrapper>
        )
    }

    return (
        <SectionWrapper column>
            <HeaderContainer>
                <div>
                    <TopSection>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <BackArrow
                                onClick={onClose}
                                style={{
                                    cursor: 'pointer',
                                    marginRight: '8px',
                                }}
                            />
                            <TopHeader>Subscribed Assessments</TopHeader>
                        </div>
                        {/* <ButtonContainer>
                            <Button.Secondary
                                text="Add Students"
                                onClick={() => setAddStudentDrawerOpen(true)}
                                icon={AddStudentIcon}
                            />
                        </ButtonContainer> */}
                    </TopSection>

                    <AssessmentInfoContainer>
                        <SubHeading>
                            {assessment?.assessmentName} &nbsp;
                            {/* Make CEFR level stand out more */}
                            {/* {cefrLevel && (
                <span
                  style={{
                    fontWeight: 'bold',
                    fontSize: '14px',
                    color: '#FFFFFF',
                    backgroundColor: '#2563EB', // Changed to blue for better visibility
                    padding: '4px 8px',
                    borderRadius: '4px',
                    marginRight: '8px',
                    display: 'inline-block',
                  }}
                >
                  CEFR Level: {formatCefrLevel(cefrLevel)}
                </span>
              )} */}
                            <span
                                style={{
                                    fontWeight: 'normal',
                                    fontSize: '14px',
                                    color: '#6B7280',
                                }}
                            >
                                {/* [Assmt. Type:{' '} */}
                                {getAssessmentTypeDisplay(assessment?.assessmentType)} (PIL)
                            </span>
                        </SubHeading>
                        {isMixMatch && mixMatchParts.length > 0 && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                                <span style={{ fontSize: 12, color: '#6B7280' }}>Assessments in this float:</span>
                                {mixMatchParts.map(part => (
                                    <button
                                        key={part.mapId}
                                        type="button"
                                        onClick={() => onOpenPart && onOpenPart(part)}
                                        title={`Open the ${partTypeLabel(part.typeName)} assessment on its own`}
                                        style={{
                                            border: '1px solid #D9D9F3',
                                            background: '#F5F5FF',
                                            color: '#4338CA',
                                            borderRadius: 6,
                                            padding: '2px 10px',
                                            fontSize: 12,
                                            fontWeight: 600,
                                            cursor: 'pointer',
                                        }}
                                    >
                                        {partTypeLabel(part.typeName)}
                                    </button>
                                ))}
                            </div>
                        )}

                        {/* You can remove this debug info once it's working */}
                    </AssessmentInfoContainer>
                </div>
                <div
                    style={{
                        marginLeft: '8px',
                        marginTop: '24px',
                        marginBottom: '14px',
                    }}
                >
                    <CollegeCard
                        name={assessment.collegeName}
                        city={assessment.collegeCity || ''}
                        state={assessment.collegeState || ''}
                        logoUrl={assessment.collegeLogoUrl}
                    />
                </div>
            </HeaderContainer>

            <ContentSection>
                {/* {allFiltersSelected ? (
          <FilterSummaryBar>
            <SummaryText>{getFilterSummaryText()}</SummaryText>
            <ClearFilterButton type="primary" onClick={handleClearFilters}>
              Clear Filters
            </ClearFilterButton>
          </FilterSummaryBar>
        ) : (
          <FormSection>
            <FormRow columns={1}> 
              <FormGroup>
                <StyledLabel>Passing Year</StyledLabel>
                <Select
                  value={passingYear}
                  onChange={e => {
                    const value = e.target.value
                    setPassingYear(value)
                    updateFilter('passingYear', value)
                  }}
                >
                  <option value="">Select Year</option>
                  {passingYears.map(year => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </Select>
              </FormGroup> 
             </FormRow>
          </FormSection>
        )} */}

                {/* Attempt funnel. Email delivery is a property of a candidate,
                    not a stage of this funnel, so it lives in the table's
                    DELIVERY column rather than competing for space here. */}
                {callScheduleMapId && <CallSchedulePanel mapId={callScheduleMapId} />}

                <StatusContainer>
                    {ATTEMPT_STAGES.map((stage, index) => (
                        <React.Fragment key={stage.key}>
                            {index > 0 && (
                                <CaretRight style={{ margin: '0 2px', flexShrink: 0 }} />
                            )}
                            <StatusItem
                                isActive={activeStatusItem === stage.key}
                                role="button"
                                tabIndex={0}
                                aria-pressed={activeStatusItem === stage.key}
                                onClick={() => handleStatusItemClick(stage.key)}
                                onKeyDown={event => {
                                    if (event.key === 'Enter' || event.key === ' ') {
                                        event.preventDefault()
                                        handleStatusItemClick(stage.key)
                                    }
                                }}
                            >
                                <div className="status-label">{stage.label}</div>
                                <div className="status-value">
                                    {attemptCounts[stage.key]}
                                </div>
                            </StatusItem>
                        </React.Fragment>
                    ))}
                </StatusContainer>

                {activeStatusItem === 'pending' && (
                    <div style={{ padding: '0px 20px', marginBottom: '20px' }}>
                        <span style={{ color: COLORS.TEXT_SECONDARY, fontSize: '14px' }}>
                            Note: Students in 'Not started' state have not started the assessment
                            yet.
                        </span>
                    </div>
                )}

                <StudentsTable
                    loading={studentDataLoading}
                    students={filteredStudents}
                    statusType={activeStatusItem}
                    totalItems={filteredStudents.length}
                    onSearch={handleSearch}
                    limit={studentsLimit}
                    page={studentsPage}
                    onPageChange={setStudentsPage}
                    filterValues={{
                        passingYear,
                        specialization,
                    }}
                    onClearFilters={handleClearFilters}
                    assessmentId={assessment?.id}
                    exportStudentData={exportStudentData}
                    sendRemindersToStudents={sendRemindersToStudents}
                    resendInvitesToStudents={resendInvitesToStudents}
                    entityType={entityType}
                    allowProctoring={assessment?.allowProctoring}
                    assessmentType={assessment?.assessmentType} // Make sure this is correctly passed
                    mixMatchParts={mixMatchParts}
                    roleBasedSections={assessmentInfo?.roleBasedSections || []}
                    searchValue={searchQuery}
                />
                <EnhancedBulkUploadDrawer
                    visible={addStudentDrawerOpen}
                    onClose={() => setAddStudentDrawerOpen(false)}
                    assessmentData={{
                        ...assessment,
                        instituteCampusId: assessmentInfo?.instituteCampusId
                    }}
                    entityType={entityType}
                    onDataSubmit={handleSaveStudents}
                    initialScreen="main_options"
                />
            </ContentSection>
        </SectionWrapper>
    )
}

export default AssessmentDetails